import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import { usePrint } from "../../hooks/usePrint";

const usePrintLogic = ({
  itemDetails,
  sectionPrintComponent,
  hiddenId,
  isTest = false,
  callBackFunc = () => {},
  onErrorCallBackFunc = () => {},
}) => {
  const {
    business_name,
    address,
    logo: logoUrl,
    receipt_footer_title,
    receipt_footer_description,
  } = useSelector((state) => state.profileDetailsReducer.businessDetails);
  const [logo, setLogo] = useState(logoUrl);
  const [isLogoReady, setIsLogoReady] = useState(!logoUrl);
  const logoBase64Ref = useRef(null);
  const { print, paperSize } = usePrint({ isTest, isEnabled: false });

  // Pre-fetch the logo as a base64 data URL so html2canvas never needs to
  // re-fetch a cross-origin URL (avoids browser-cache/CORS taint issues).
  useEffect(() => {
    if (!logoUrl) return;
    let cancelled = false;
    setIsLogoReady(false);
    fetch(logoUrl, { mode: "cors" })
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }),
      )
      .then((dataUrl) => {
        if (!cancelled) {
          logoBase64Ref.current = dataUrl;
          setLogo(dataUrl);
          setIsLogoReady(true);
        }
      })
      .catch(() => {
        // Server doesn't support CORS for fetch/XHR reads — fall back to
        // the original URL. Plain <img> loading (no crossOrigin) still
        // works even when the host has no CORS headers at all, since
        // CORS only gates JS pixel access, not basic image display.
        if (!cancelled) {
          setLogo(logoUrl);
          setIsLogoReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  const handlePrint = async () => {
    if (!sectionPrintComponent.current) return;

    // html2canvas captures whatever is currently painted — if the logo
    // <img> hasn't finished loading/decoding yet, it prints blank. Wait
    // for it (bounded, so a broken image URL can't hang printing).
    const logoImg = sectionPrintComponent.current.querySelector("img");
    if (logoImg && !logoImg.complete) {
      await new Promise((resolve) => {
        const timeout = setTimeout(resolve, 3000);
        logoImg.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        logoImg.onerror = () => {
          clearTimeout(timeout);
          resolve();
        };
      });
    }

    const canvas = await html2canvas(sectionPrintComponent.current, {
      onclone: (clonedDoc) => {
        const clonedNode = clonedDoc.getElementById(hiddenId);
        if (!clonedNode) return;

        clonedNode.style.display = "block";
        clonedNode.style.backgroundColor = "#ffffff";

        // Use actual content height only — no minimum paperSize enforced
        const height = clonedNode.scrollHeight;
        clonedNode.style.height = `${height + 5}px`;

        const allElements = clonedNode.querySelectorAll("*");
        allElements.forEach((el) => {
          el.style.color = "#000000";
          el.style.webkitPrintColorAdjust = "exact";
          el.style.printColorAdjust = "exact";
        });
      },

      backgroundColor: "#ffffff",
      width: paperSize,
      scale: 2,
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");

    print({
      imageData: imgData,
      onSuccess: callBackFunc,
      onError: onErrorCallBackFunc,
    });
  };

  useEffect(() => {
    if (itemDetails && isLogoReady) handlePrint();
  }, [itemDetails, isLogoReady]);

  return {
    paperSize,
    business_name,
    address,
    logo,
    receipt_footer_title,
    receipt_footer_description,
  };
};

export default usePrintLogic;
